/**
 * A calendar's days: which ones a week or a month covers, and which bookings
 * fall on each. Shared by the therapist's scheduler and the centre agenda, so
 * both draw a month the same way.
 *
 * Kept free of React so the arithmetic can be tested without rendering.
 */

export type CalendarView = "week" | "month";

/** `YYYY-MM-DD` → a UTC date, so shifting it never trips over DST. */
function parseDateKey(dateKey: string): Date {
    const [year, month, day] = dateKey.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}

function formatDateKey(date: Date): string {
    return date.toISOString().slice(0, 10);
}

/** `dateKey` moved by `days`, which may be negative. */
export function shiftDays(dateKey: string, days: number): string {
    const date = parseDateKey(dateKey);
    date.setUTCDate(date.getUTCDate() + days);
    return formatDateKey(date);
}

/** The Monday of the week `dateKey` falls in; Sunday closes its week. */
export function mondayOf(dateKey: string): string {
    const weekday = (parseDateKey(dateKey).getUTCDay() + 6) % 7;
    return shiftDays(dateKey, -weekday);
}

/** The seven day keys of the week starting on `monday`. */
export function weekDays(monday: string): string[] {
    return Array.from({ length: 7 }, (_, index) => shiftDays(monday, index));
}

/**
 * The first and last day the view on screen covers around `anchor`: Monday
 * to Sunday for a week, the first to the last of the month for a month.
 */
export function calendarRange(
    view: CalendarView,
    anchor: string
): { from: string; to: string } {
    if (view === "week") {
        const monday = mondayOf(anchor);
        return { from: monday, to: shiftDays(monday, 6) };
    }
    const date = parseDateKey(anchor);
    const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
    return { from: formatDateKey(first), to: formatDateKey(last) };
}

/** The anchor one week or one month on (`step` 1) or back (`step` -1). */
export function shiftAnchor(
    view: CalendarView,
    anchor: string,
    step: number
): string {
    if (view === "week") return shiftDays(anchor, 7 * step);
    const date = parseDateKey(anchor);
    // The first of the month, so 31 January plus a month is not 3 March.
    return formatDateKey(
        new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + step, 1))
    );
}

/**
 * Monday-first weeks covering the month of `anchor`, padded with null where a
 * week starts before the first or ends after the last.
 */
export function monthWeeks(anchor: string): (string | null)[][] {
    const { from, to } = calendarRange("month", anchor);
    const lead = (parseDateKey(from).getUTCDay() + 6) % 7;
    const cells: (string | null)[] = Array(lead).fill(null);
    for (let day = from; day <= to; day = shiftDays(day, 1)) cells.push(day);
    while (cells.length % 7 !== 0) cells.push(null);
    return Array.from({ length: cells.length / 7 }, (_, index) =>
        cells.slice(index * 7, index * 7 + 7)
    );
}

/** `HH:MM` or `HH:MM:SS` → minutes since midnight. */
export function minutesOfTime(time: string): number {
    const [hours, minutes] = time.split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
    return hours * 60 + minutes;
}

/** The fields of a booking the calendar places it by. */
export interface CalendarBooking {
    /** The booking's day; a naive date or datetime, read as its first ten characters. */
    date: string;
    hour: string;
    duration: number;
}

/** `YYYY-MM-DD` → that day's bookings, ascending by hour. */
export function bookingsByDay<T extends CalendarBooking>(
    bookings: readonly T[]
): Map<string, T[]> {
    const byDay = new Map<string, T[]>();
    bookings.forEach((booking) => {
        const day = booking.date.slice(0, 10);
        byDay.set(day, [...(byDay.get(day) ?? []), booking]);
    });
    byDay.forEach((dayBookings) =>
        dayBookings.sort((a, b) => minutesOfTime(a.hour) - minutesOfTime(b.hour))
    );
    return byDay;
}

/**
 * Whether a booking has already ended, against the viewer's clock: the day
 * is before today, or it is today and its last minute has gone.
 */
export function isBookingPast(
    booking: CalendarBooking,
    todayKey: string,
    nowMinute: number
): boolean {
    const day = booking.date.slice(0, 10);
    if (day < todayKey) return true;
    if (day > todayKey) return false;
    return minutesOfTime(booking.hour) + booking.duration <= nowMinute;
}

/** `María García` → `MG`, for a card too narrow for a whole name. */
export function initialsOf(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
}

/** `HH:MM` for minutes since midnight. */
export function minuteLabel(minute: number): string {
    const pad = (value: number) => `${value}`.padStart(2, "0");
    return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;
}
